import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
    useId,
} from 'react';
import type { GtChartProps } from './GtChartTypes';
import {
    MARGIN_TOP, MARGIN_BOT, MARGIN_RIGHT, MIN_X_LBL_PX,
    ZOOM_FACTOR, MIN_ZOOM_WIN, DEFAULT_COLORS,
    computeTicks, fmtNum, smoothBezier, arcPath,
    _isDateLike, _autoFmtShort, _autoFmtFull,
    type ZoomRange,
} from './GtChartHelpers';
import { GtChartTooltip } from './GtChartTooltip';
import type { GtTooltipRow } from './GtChartTooltip';
import { GtChartLegend } from './GtChartLegend';
import type { GtLegendItem } from './GtChartLegend';

// ── Gradient helpers — solid color shifts, no opacity, works on any bg ────────
const _lighten = (hex: string, amt: number): string => {
    const c = hex.replace('#', '');
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    return '#' + [
        Math.round(r + (255 - r) * amt),
        Math.round(g + (255 - g) * amt),
        Math.round(b + (255 - b) * amt),
    ].map((v) => v.toString(16).padStart(2, '0')).join('');
};

// ── GtChart ──────────────────────────────────────────────────────────────────
const GtChart: React.FC<GtChartProps> = ({
    type = 'bar',
    series,
    categories = [],
    height = 240,
    colors,
    isDark = false,
    animate = true,
    enableZoom = true,
    formatY,
    formatX,
    formatTooltipValue,
    formatTooltipTitle,
    showGrid = true,
    tickCount = 5,
    minY = 0,
    referenceLines,
    showLegend,
    legendPosition = 'bottom',
    legendScale = 1,
    highlightOnHover = false,
    hoverDimOpacity = 0.28,
    crosshairColor,
    strokeWidth = 2,
    fillOpacity = 0.18,
    barRadius = 4,
    barGroupPadding = 0.28,
    stacked = false,
    smooth = 0.3,
    dotSize,
    showDataLabels = false,
    showTotalsLabel = false,
    totalsLabelColor,
    stackGroups,
    stackGroupLabels,
    tooltipTotals,
    barGradient = false,
    onDataPointClick,
    className = '',
    noDataLabel = 'No data',
    donutInnerRatio = 0.55,
    donutLabel,
    pieLabelFontSize = 11,
    pieLabelMinAngle = 0.35,
    donutCenterFontSize = 22,
    emptyStateFill,
}) => {
    const uid = useId();
    const rawId = uid.replace(/:/g, '');
    const clipId = `vms-clip-${rawId}`;

    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [containerW, setContainerW] = useState(500);
    const [mounted, setMounted] = useState(false);
    const [zoom, setZoom] = useState<ZoomRange | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [hoveredColIdx, setHoveredColIdx] = useState<number | null>(null);
    const dragRef = useRef<{ mouseX: number; zStart: number; zEnd: number } | null>(null);
    const touchRef = useRef<{
        type: 'pinch' | 'pan';
        dist?: number;
        midFrac?: number;
        touchX?: number;
        zStart: number;
        zEnd: number;
    } | null>(null);

    // ── Tooltip state ──────────────────────────────────────────────────────────
    // Split into two parts:
    //   tipPos  → ref (updated on every mouse move, no re-render)
    //   catIdx / visible → state (only changes on column boundary or enter/leave)
    // This eliminates full-chart re-renders from sub-pixel cursor movements.
    const tipPosRef = useRef({ cx: 0, cy: 0 });
    const [tipCat, setTipCat] = useState<{ visible: boolean; catIdx: number; pieSlice?: number }>(
        { visible: false, catIdx: -1 },
    );

    // ── Measure container ──────────────────────────────────────────────────────
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const update = () => {
            const w = el.getBoundingClientRect().width;
            if (w > 0) setContainerW(w);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // ── Stable animation key (avoids inline .map().join() in dep array) ────────
    const seriesNamesKey = useMemo(() => series.map((s) => s.name).join('|'), [series]);

    // ── Entry animation trigger ────────────────────────────────────────────────
    useEffect(() => {
        if (!animate) {
            setMounted(true);
            return;
        }
        setMounted(false);
        let raf1: number, raf2: number;
        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => setMounted(true));
        });
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
        // Re-animate when series names change (new data set)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animate, type, seriesNamesKey]);

    // ── Derived flags ──────────────────────────────────────────────────────────
    const isPie = type === 'pie' || type === 'donut';
    const isStacked = stacked || type === 'stacked';
    const isZoomed = zoom !== null;
    const zoomEnabled = enableZoom && !isPie;

    // ── Resolved colors ────────────────────────────────────────────────────────
    const resolvedColors = useMemo((): string[] => {
        const palette = colors?.length ? colors : DEFAULT_COLORS;
        return series.map((s, i) => s.color ?? palette[i % palette.length]);
    }, [series, colors]);

    // ── Empty check ────────────────────────────────────────────────────────────
    const hasData = useMemo(
        () => series.some((s) => s.data.some((v) => typeof v === 'number' && v !== 0)),
        [series],
    );

    // ── Legend ────────────────────────────────────────────────────────────────
    const showLeg = showLegend ?? (series.length > 1 || isPie);
    const legRight = showLeg && legendPosition === 'right';
    const legBottom = showLeg && legendPosition === 'bottom';
    const legTop = showLeg && legendPosition === 'top';
    const legendRW = legRight ? 108 : 0;

    // ── Y range ──────────────────────────────────────────────────────────────
    const n = useMemo(
        () => Math.max(categories.length, ...series.map((s) => s.data.length), 1),
        [categories, series],
    );

    const rawMaxY = useMemo(() => {
        if (isPie) return 1;
        // stackGroups: Y-max is the tallest sub-bar across all groups and columns
        if (stackGroups?.length) {
            let m = 0;
            for (let ci = 0; ci < n; ci++) {
                for (const grp of stackGroups) {
                    const sum = grp.reduce((acc, si) => acc + (series[si]?.data[ci] ?? 0), 0);
                    if (sum > m) m = sum;
                }
            }
            return m;
        }
        if (isStacked) {
            let m = 0;
            for (let i = 0; i < n; i++) {
                const sum = series.reduce((acc, s) => acc + (s.data[i] ?? 0), 0);
                if (sum > m) m = sum;
            }
            return m;
        }
        return Math.max(...series.flatMap((s) => s.data.filter(Number.isFinite) as number[]), 0);
    }, [series, isStacked, isPie, n, stackGroups]);

    const { ticks, niceMax } = useMemo(() => computeTicks(Math.max(rawMaxY, minY), tickCount), [rawMaxY, minY, tickCount]);
    const yFmt = formatY ?? fmtNum;

    // ── Stacked top-series per column (memoized — O(n×series) but only recomputed on data change) ──
    const topSeriesPerColMemo = useMemo((): number[] | null => {
        if (!isStacked) return null;
        return Array.from({ length: n }, (_, ci) => {
            for (let k = series.length - 1; k >= 0; k--) {
                if ((series[k].data[ci] ?? 0) > 0) return k;
            }
            return -1;
        });
    }, [isStacked, n, series]);

    // ── Dynamic left margin (widest Y label × ~7px/char + padding) ─────────────
    const marginLeft = useMemo(() => {
        const longest = Math.max(...ticks.map(t => yFmt(t).length), 1);
        return Math.max(36, longest * 7 + 18);
    }, [ticks, yFmt]);

    // ── SVG / chart geometry ─────────────────────────────────────────────────
    const svgW = containerW - legendRW;
    const svgH = height;
    const chartW = Math.max(1, svgW - marginLeft - MARGIN_RIGHT);
    const chartH = Math.max(1, svgH - MARGIN_TOP - MARGIN_BOT);

    // ── Zoom (clamped) ────────────────────────────────────────────────────────
    const { start: zStart, end: zEnd } = useMemo((): ZoomRange => {
        if (!zoom) return { start: 0, end: n - 1 };
        return { start: Math.max(0, zoom.start), end: Math.min(n - 1, zoom.end) };
    }, [zoom, n]);
    const zWindow = Math.max(0, zEnd - zStart);

    // ── Scale functions (zoom-aware) ─────────────────────────────────────────
    const yToSvg = useCallback(
        (v: number) => MARGIN_TOP + chartH * (1 - Math.max(0, v) / niceMax),
        [chartH, niceMax],
    );
    // Left edge of band i
    const xToSvg = useCallback(
        (i: number) => marginLeft + ((i - zStart) / Math.max(zWindow + 1, 1)) * chartW,
        [marginLeft, chartW, zStart, zWindow],
    );
    // Width of one band in the zoom window
    const xBandW = chartW / Math.max(zWindow + 1, 1);
    // Center x of band i
    const xCenter = useCallback(
        (i: number) => xToSvg(i) + xBandW * 0.5,
        [xToSvg, xBandW],
    );
    // SVG x → nearest data index
    const svgXToIdx = useCallback(
        (svgX: number) => {
            const frac = (svgX - marginLeft) / chartW;
            return Math.max(0, Math.min(n - 1, Math.floor(zStart + frac * (zWindow + 1))));
        },
        [marginLeft, chartW, zStart, zWindow, n],
    );

    // ── X labels — auto-format dates when no formatX provided ────────────────
    // Ignore empty strings (null range from API → '' via chartOptions) so one
    // bad item does not suppress formatting for the whole chart.
    const autoDateCats = useMemo(() => {
        const nonEmpty = categories.filter((c) => String(c).trim() !== '');
        return nonEmpty.length > 0 && nonEmpty.every((c) => _isDateLike(c));
    }, [categories]);

    const xLabels = useMemo(() => {
        const base = categories.length > 0
            ? categories.map((c, i) => {
                if (formatX) return String(formatX(c, i));
                if (autoDateCats) return _autoFmtShort(c);
                return String(c);
            })
            : Array.from({ length: n }, (_, i) => String(i + 1));
        return base;
    }, [categories, n, formatX, autoDateCats]);

    const xLabelStep = useMemo(() => {
        // Compute the pixel width of the widest label (actual render width, no padding).
        // Two-line week labels: only the longer line determines horizontal width.
        // ~7.2 px/char at fontSize 10 is a conservative estimate for SVG text.
        const maxLblW = xLabels.reduce((mx, lbl) => {
            const w = Math.max(...lbl.split('\n').map((l) => l.length)) * 7.2;
            return Math.max(mx, w);
        }, 32); // 32px absolute minimum label width
        // Guarantee at least 24 px of blank space between any two adjacent labels
        // (12 px clear on each side), regardless of band width.
        const requiredSpacing = maxLblW + 24;
        return Math.max(1, Math.ceil(requiredSpacing / xBandW));
    }, [xBandW, xLabels]);

    // Visible index range with 1-slot padding
    const viStart = Math.max(0, Math.floor(zStart) - 1);
    const viEnd = Math.min(n - 1, Math.ceil(zEnd) + 1);

    // ── Tooltip data ─────────────────────────────────────────────────────────
    const tooltipRows = useMemo((): GtTooltipRow[] => {
        if (!tipCat.visible) return [];
        if (isPie) {
            const si = tipCat.pieSlice ?? 0;
            return [{
                name: xLabels[si] ?? series[0]?.name ?? `Slice ${si + 1}`,
                value: series[0]?.data[si] ?? 0,
                color: resolvedColors[si % resolvedColors.length],
            }];
        }
        if (tipCat.catIdx < 0) return [];
        return series.map((s, i) => ({
            name: s.name,
            value: s.data[tipCat.catIdx] ?? 0,
            color: resolvedColors[i],
        }));
    }, [tipCat, series, resolvedColors, isPie, xLabels]);

    const tooltipCatLabel = useMemo((): string => {
        const catIdx = isPie ? (tipCat.pieSlice ?? 0) : tipCat.catIdx;
        const rawCat = categories[catIdx] ?? catIdx;
        if (formatTooltipTitle) return formatTooltipTitle(rawCat, catIdx);
        if (autoDateCats) return _autoFmtFull(rawCat);
        if (isPie) return xLabels[tipCat.pieSlice ?? 0] ?? `Slice ${(tipCat.pieSlice ?? 0) + 1}`;
        return xLabels[tipCat.catIdx] ?? String(tipCat.catIdx);
    }, [tipCat, isPie, categories, formatTooltipTitle, autoDateCats, xLabels]);

    // ── Legend items ──────────────────────────────────────────────────────────
    const legendItems = useMemo((): GtLegendItem[] => {
        if (isPie) {
            return (series[0]?.data ?? []).map((_, i) => ({
                name: xLabels[i] ?? `Slice ${i + 1}`,
                color: resolvedColors[i % resolvedColors.length],
                total: null,
            }));
        }
        return series.map((s, i) => ({
            name: s.name,
            color: resolvedColors[i],
            total: (s.data ?? []).reduce((acc, v) => acc + (v ?? 0), 0),
        }));
    }, [series, isPie, xLabels, resolvedColors]);

    // ── Non-passive wheel → zoom ─────────────────────────────────────────────
    useEffect(() => {
        const el = svgRef.current;
        if (!el || !zoomEnabled) return;
        const handler = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const rect = el.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const frac = (mouseX - marginLeft) / chartW;
            const cursor = zStart + frac * (zWindow + 1);
            const dir = e.deltaY > 0 ? 1 : -1;
            const factor = dir > 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR;
            const newWin = Math.max(MIN_ZOOM_WIN, (zWindow + 1) * factor - 1);
            let ns = cursor - frac * (newWin + 1);
            let ne = ns + newWin;
            if (ns < 0) { ne -= ns; ns = 0; }
            if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
            ns = Math.max(0, ns); ne = Math.min(n - 1, ne);
            if (newWin >= n - 1 && ns <= 0.01) { setZoom(null); return; }
            setZoom({ start: ns, end: ne });
        };
        el.addEventListener('wheel', handler, { passive: false });
        return () => el.removeEventListener('wheel', handler);
    }, [zoomEnabled, marginLeft, chartW, zStart, zWindow, n]);

    // ── Touch pinch-to-zoom / single-finger pan ────────────────────────────────
    useEffect(() => {
        const el = svgRef.current;
        if (!el || !zoomEnabled) return;

        function getTouchDist(touches: TouchList) {
            const dx = touches[1].clientX - touches[0].clientX;
            const dy = touches[1].clientY - touches[0].clientY;
            return Math.hypot(dx, dy);
        }

        function onTouchStart(e: TouchEvent) {
            e.preventDefault();
            const rect = el!.getBoundingClientRect();
            if (e.touches.length === 2) {
                const dist = getTouchDist(e.touches);
                const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
                const midFrac = (midX - marginLeft) / chartW;
                touchRef.current = { type: 'pinch', dist, midFrac, zStart, zEnd };
            } else if (e.touches.length === 1) {
                touchRef.current = { type: 'pan', touchX: e.touches[0].clientX, zStart, zEnd };
            }
        }

        function onTouchMove(e: TouchEvent) {
            e.preventDefault();
            if (!touchRef.current) return;
            const st = touchRef.current;
            if (st.type === 'pinch' && e.touches.length === 2) {
                const newDist = getTouchDist(e.touches);
                const factor = (st.dist ?? newDist) / newDist;
                const curWin = st.zEnd - st.zStart;
                const newWin = Math.max(MIN_ZOOM_WIN, (curWin + 1) * factor - 1);
                const cursor = st.zStart + (st.midFrac ?? 0.5) * (curWin + 1);
                let ns = cursor - (st.midFrac ?? 0.5) * (newWin + 1);
                let ne = ns + newWin;
                if (ns < 0) { ne -= ns; ns = 0; }
                if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
                ns = Math.max(0, ns); ne = Math.min(n - 1, ne);
                if (newWin >= n - 1 && ns <= 0.01) { setZoom(null); return; }
                setZoom({ start: ns, end: ne });
            } else if (st.type === 'pan' && e.touches.length === 1) {
                const dx = e.touches[0].clientX - (st.touchX ?? e.touches[0].clientX);
                const dIdx = (dx / chartW) * (st.zEnd - st.zStart + 1);
                let ns = st.zStart - dIdx;
                let ne = st.zEnd - dIdx;
                if (ns < 0) { ne -= ns; ns = 0; }
                if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
                setZoom({ start: Math.max(0, ns), end: Math.min(n - 1, ne) });
            }
        }

        function onTouchEnd(e: TouchEvent) {
            if (e.touches.length === 0) touchRef.current = null;
            else if (e.touches.length < 2 && touchRef.current?.type === 'pinch') touchRef.current = null;
        }

        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
        };
    }, [zoomEnabled, marginLeft, chartW, zStart, zEnd, n]);

    // ── Mouse event handlers ─────────────────────────────────────────────────
    const getContainerXY = useCallback((clientX: number, clientY: number) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return { cx: 0, cy: 0 };
        return { cx: clientX - rect.left, cy: clientY - rect.top };
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
        if (!zoomEnabled || !isZoomed) return;
        e.preventDefault();
        dragRef.current = { mouseX: e.clientX, zStart, zEnd };
        setIsDragging(true);
    }, [zoomEnabled, isZoomed, zStart, zEnd]);

    const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
        const svgRect = e.currentTarget.getBoundingClientRect();
        const svgX = e.clientX - svgRect.left;
        const { cx, cy } = getContainerXY(e.clientX, e.clientY);
        if (isDragging && dragRef.current) {
            const dx = e.clientX - dragRef.current.mouseX;
            const dIdx = (dx / chartW) * (zWindow + 1);
            let ns = dragRef.current.zStart - dIdx;
            let ne = dragRef.current.zEnd - dIdx;
            if (ns < 0) { ne -= ns; ns = 0; }
            if (ne > n - 1) { ns -= ne - (n - 1); ne = n - 1; }
            setZoom({ start: Math.max(0, ns), end: Math.min(n - 1, ne) });
            return;
        }
        if (svgX >= marginLeft && svgX <= marginLeft + chartW) {
            const idx = svgXToIdx(svgX);
            // Always update position via ref (zero re-render cost)
            tipPosRef.current = { cx, cy };
            // Only trigger state update (re-render) when the column changes
            setTipCat(prev => {
                const needsUpdate = !prev.visible || prev.catIdx !== idx;
                return needsUpdate ? { visible: true, catIdx: idx } : prev;
            });
            if (highlightOnHover) setHoveredColIdx(prev => prev === idx ? prev : idx);
        } else {
            tipPosRef.current = { cx, cy };
            setTipCat(prev => prev.visible ? { ...prev, visible: false } : prev);
            if (highlightOnHover) setHoveredColIdx(prev => prev === null ? prev : null);
        }
    }, [getContainerXY, isDragging, chartW, zWindow, n, marginLeft, svgXToIdx, highlightOnHover]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) { setIsDragging(false); dragRef.current = null; }
    }, [isDragging]);

    const handleSvgLeave = useCallback(() => {
        setTipCat(prev => prev.visible ? { ...prev, visible: false } : prev);
        if (highlightOnHover) setHoveredColIdx(null);
        if (isDragging) { setIsDragging(false); dragRef.current = null; }
    }, [isDragging, highlightOnHover]);

    const handleDblClick = useCallback(() => {
        if (zoomEnabled && isZoomed) setZoom(null);
    }, [zoomEnabled, isZoomed]);

    // ── Theme tokens (memoized — only recompute when isDark changes) ───────────
    const themeTokens = useMemo(() => ({
        gridColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        axisColor: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.09)',
        labelColor: isDark ? '#94a3b8' : '#64748b',
        btnBg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        btnColor: isDark ? '#94a3b8' : '#64748b',
        btnBorder: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
        dotStroke: isDark ? '#0d1520' : '#ffffff',
    }), [isDark]);
    const { gridColor, axisColor, labelColor, btnBg, btnColor, btnBorder,
        dotStroke } = themeTokens;

    // ──────────────────────────────────────────────────────────────────────────
    // RENDERERS
    // ──────────────────────────────────────────────────────────────────────────

    // ── Grid ─────────────────────────────────────────────────────────────────
    function renderGrid() {
        if (!showGrid) return null;
        return (
            <g>
                {ticks.map((t) => (
                    <line key={t}
                        x1={marginLeft} x2={marginLeft + chartW}
                        y1={yToSvg(t)} y2={yToSvg(t)}
                        stroke={t === 0 ? axisColor : gridColor}
                        strokeWidth={t === 0 ? 1.5 : 1}
                        strokeDasharray={t === 0 ? undefined : '4 3'}
                    />
                ))}
                {/* Y axis rule */}
                <line x1={marginLeft} x2={marginLeft}
                    y1={MARGIN_TOP} y2={MARGIN_TOP + chartH}
                    stroke={axisColor} strokeWidth={1} />
                {/* X axis baseline */}
                <line x1={marginLeft} x2={marginLeft + chartW}
                    y1={MARGIN_TOP + chartH} y2={MARGIN_TOP + chartH}
                    stroke={axisColor} strokeWidth={1} />
            </g>
        );
    }

    // ── Y axis labels ────────────────────────────────────────────────────────
    function renderYAxis() {
        return (
            <g>
                {ticks.map((t) => (
                    <text key={t}
                        x={marginLeft - 8} y={yToSvg(t)}
                        textAnchor="end" dominantBaseline="central"
                        fontSize={10} fill={labelColor} fontFamily="inherit"
                    >
                        {yFmt(t)}
                    </text>
                ))}
            </g>
        );
    }

    // ── X axis labels ────────────────────────────────────────────────────────
    function renderXAxis() {
        const y = MARGIN_TOP + chartH + 14;
        return (
            <g>
                {xLabels.map((label, i) => {
                    if (i < viStart || i > viEnd) return null;
                    // Show labels on the regular step grid.
                    // Only force the last label when it is at least half a step away
                    // from the previous natural step — prevents it crowding its neighbour.
                    const isLast = i === n - 1;
                    const distFromLastStep = (n - 1) % xLabelStep;
                    const forceLast = isLast && distFromLastStep > 0 && distFromLastStep >= Math.ceil(xLabelStep / 2);
                    if (i % xLabelStep !== 0 && !forceLast) return null;
                    const x = xCenter(i);
                    if (x < marginLeft - 4 || x > marginLeft + chartW + 4) return null;
                    // px-per-char at fontSize 10 — match the step calculation estimate
                    const maxC = Math.max(3, Math.floor((xBandW * xLabelStep) / 7.2));
                    // Week labels are two lines ("Jun 27\nJul 3"); trim each line separately
                    const lines = label.split('\n');
                    const trimLine = (s: string) => s.length > maxC ? `${s.slice(0, maxC - 1)}…` : s;
                    const isWeek = lines.length > 1;
                    return (
                        <text key={i} x={x} y={y}
                            textAnchor="middle" dominantBaseline="hanging"
                            fontSize={isLast ? 10.5 : 10}
                            fontWeight={isLast ? 600 : 400}
                            fill={isLast ? (isDark ? '#e2e8f0' : '#1e293b') : labelColor}
                            fontFamily="inherit"
                        >
                            <tspan x={x}>{trimLine(lines[0])}</tspan>
                            {isWeek && (
                                <tspan x={x} dy={12}
                                    fontSize={8.5}
                                    fill={isLast ? (isDark ? '#94a3b8' : '#94a3b8') : labelColor}
                                    opacity={0.7}
                                >
                                    {trimLine(lines[1])}
                                </tspan>
                            )}
                        </text>
                    );
                })}
            </g>
        );
    }

    // ── Crosshair ────────────────────────────────────────────────────────────
    function renderCrosshair() {
        if (!tipCat.visible || isPie || tipCat.catIdx < 0) return null;
        const x = xCenter(tipCat.catIdx);
        const bx = xToSvg(tipCat.catIdx);
        if (x < marginLeft - 1 || x > marginLeft + chartW + 1) return null;
        const bxC = Math.max(marginLeft, bx);
        const bwC = Math.min(xBandW, marginLeft + chartW - bxC);
        const crossColor = crosshairColor ?? resolvedColors[0] ?? (isDark ? '#64748b' : '#94a3b8');
        return (
            <g style={{ pointerEvents: 'none' }}>
                {/* Active column band highlight */}
                <rect x={bxC + 1} y={MARGIN_TOP} rx={3}
                    width={Math.max(0, bwC - 2)} height={chartH}
                    fill={isDark ? 'rgba(255,255,255,0.055)' : 'rgba(0,0,0,0.042)'}
                />
                {/* Vertical cursor line — tinted with primary series color */}
                <line x1={x} x2={x} y1={MARGIN_TOP} y2={MARGIN_TOP + chartH}
                    stroke={crossColor}
                    strokeWidth={1.5} opacity={0.38}
                />
            </g>
        );
    }

    // ── Zoom reset chip (shown only when zoomed; no +/− buttons — use pinch/wheel) ──
    function renderZoomControls() {
        if (!zoomEnabled || n <= 1 || !isZoomed) return null;
        return (
            <div style={{
                position: 'absolute', top: 4, right: MARGIN_RIGHT,
                display: 'flex', alignItems: 'center', gap: 3, zIndex: 10,
            }}>
                <button
                    onClick={() => setZoom(null)}
                    title="Double-click or double-tap to reset zoom"
                    style={{
                        height: 22, borderRadius: 7, paddingInline: 8,
                        border: `1px solid ${btnBorder}`,
                        background: btnBg, color: btnColor,
                        cursor: 'pointer', fontSize: 10, lineHeight: '1',
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontFamily: 'inherit', flexShrink: 0,
                        letterSpacing: '0.01em',
                    }}
                >
                    <span style={{ fontSize: 12, lineHeight: 1 }}>↺</span>
                    <span style={{ fontSize: 9, fontVariantNumeric: 'tabular-nums', opacity: 0.75 }}>
                        {Math.round(zStart + 1)}–{Math.round(zEnd + 1)} / {n}
                    </span>
                </button>
            </div>
        );
    }

    // ── Bar series ────────────────────────────────────────────────────────────
    function renderBars() {
        if (stackGroups?.length) return renderGroupedStackedBars(stackGroups);

        // Cap bar width so very few data points don't produce enormous bars.
        const MAX_BAR_PX = 72;
        const rawGroupW = Math.max(1, xBandW * (1 - barGroupPadding));
        const groupW = Math.max(1, Math.min(rawGroupW, MAX_BAR_PX * (isStacked ? 1 : series.length)));
        const groupPad = xBandW - groupW;
        const barW = isStacked ? groupW : Math.max(1, groupW / series.length);

        const topSeriesPerCol = topSeriesPerColMemo;

        // ── Stacked column totals (for total label above bar) ──────────────────
        const colTotals: number[] = isStacked
            ? Array.from({ length: n }, (_, ci) =>
                series.reduce((s, ser) => s + (ser.data[ci] ?? 0), 0))
            : [];

        const barElements = series.map((s, si) => {
            const color = resolvedColors[si];
            const barGradId = `bar-grad-${rawId}-${si}`;

            return (
                <g key={`bar-${si}`} clipPath={`url(#${clipId})`}>
                    {barGradient && (
                        <defs>
                            <linearGradient id={barGradId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={_lighten(color, 0.22)} stopOpacity={1} />
                                <stop offset="20%" stopColor={color} stopOpacity={1} />
                                <stop offset="100%" stopColor={color} stopOpacity={1} />
                            </linearGradient>
                        </defs>
                    )}
                    {s.data.map((v, ci) => {
                        if (ci < viStart || ci > viEnd) return null;
                        if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null;

                        let base = 0;
                        if (isStacked) {
                            for (let k = 0; k < si; k++) base += (series[k].data[ci] ?? 0);
                        }
                        const topV = v + base;
                        const barH = Math.max(1, yToSvg(base) - yToSvg(topV));
                        const barTopY = yToSvg(topV);
                        const groupLeft = xToSvg(ci) + groupPad / 2;
                        const bx = isStacked ? groupLeft : groupLeft + si * barW;
                        const bw = Math.max(1, isStacked ? groupW : barW - 1);
                        const isTopSeg = !isStacked || topSeriesPerCol![ci] === si;
                        const rx = isTopSeg ? Math.min(barRadius, barH / 2, bw / 4) : 0;
                        const delay = animate ? ci * 0.014 + si * 0.022 : 0;
                        const bRight = bx + bw;
                        const bBottom = barTopY + barH;
                        const barPath = rx > 0
                            ? `M ${bx + rx} ${barTopY} H ${bRight - rx} Q ${bRight} ${barTopY} ${bRight} ${barTopY + rx} V ${bBottom} H ${bx} V ${barTopY + rx} Q ${bx} ${barTopY} ${bx + rx} ${barTopY} Z`
                            : `M ${bx} ${barTopY} H ${bRight} V ${bBottom} H ${bx} Z`;

                        // Highlight hovered column; dim others when highlightOnHover
                        const isHoveredCol = hoveredColIdx === ci;
                        const barOpacity = highlightOnHover && hoveredColIdx !== null
                            ? (isHoveredCol ? 1 : hoverDimOpacity)
                            : 1;

                        return (
                            <React.Fragment key={`bar-${si}-${ci}`}>
                                <path
                                    d={barPath}
                                    fill={barGradient ? `url(#${barGradId})` : color}
                                    opacity={barOpacity}
                                    style={{
                                        transformBox: 'fill-box',
                                        transformOrigin: '50% 100%',
                                        transform: `scaleY(${mounted ? 1 : 0})`,
                                        transition: animate
                                            ? `transform 0.46s cubic-bezier(0.34,1.25,0.64,1) ${delay}s, opacity 0.15s ease`
                                            : 'opacity 0.15s ease',
                                        cursor: onDataPointClick ? 'pointer' : 'default',
                                    }}
                                    onClick={onDataPointClick ? () => onDataPointClick(si, ci, v) : undefined}
                                />
                                {/* White sliver between stacked segments — dramatically improves readability */}
                                {isStacked && base > 0 && barH > 2 && (
                                    <line
                                        x1={bx + 1} x2={bRight - 1}
                                        y1={barTopY + barH} y2={barTopY + barH}
                                        stroke="rgba(255,255,255,0.5)"
                                        strokeWidth={0.75}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                    {/* Per-bar data labels */}
                    {showDataLabels && s.data.map((v, ci) => {
                        if (ci < viStart || ci > viEnd) return null;
                        if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null;
                        let base = 0;
                        if (isStacked) for (let k = 0; k < si; k++) base += (series[k].data[ci] ?? 0);
                        const topV = v + base;
                        const groupLeft = xToSvg(ci) + (xBandW - groupW) / 2;
                        const bx = isStacked ? groupLeft : groupLeft + si * barW;
                        const bw = Math.max(1, isStacked ? groupW : barW - 1);
                        const cx = bx + bw / 2;
                        const lblY = yToSvg(topV) - 4;
                        return (
                            <text key={`dlbl-${si}-${ci}`} x={cx} y={lblY}
                                textAnchor="middle" dominantBaseline="auto"
                                fontSize={9} fontWeight={600} fill={color}
                                fontFamily="inherit" style={{ pointerEvents: 'none' }}
                            >{yFmt(v)}</text>
                        );
                    })}
                </g>
            );
        });

        // ── Stacked column total labels (rendered above all series) ────────────
        const totalLabels = (isStacked && showTotalsLabel) ? (
            <g style={{ pointerEvents: 'none' }}>
                {colTotals.map((total, ci) => {
                    if (ci < viStart || ci > viEnd) return null;
                    if (total <= 0) return null;
                    const groupLeft = xToSvg(ci) + groupPad / 2;
                    const cx = groupLeft + groupW / 2;
                    const topY = yToSvg(total);
                    return (
                        <text key={`total-${ci}`}
                            x={cx} y={topY - 5}
                            textAnchor="middle" dominantBaseline="auto"
                            fontSize={9.5} fontWeight={700}
                            fill={totalsLabelColor ?? (isDark ? '#64748b' : '#94a3b8')}
                            fontFamily="inherit"
                            opacity={mounted ? 1 : 0}
                            style={{ transition: animate ? 'opacity 0.4s ease 0.3s' : 'none' }}
                        >
                            {yFmt(total)}
                        </text>
                    );
                })}
            </g>
        ) : null;

        return <>{barElements}{totalLabels}</>;
    }

    // ── Grouped-stacked bars (e.g. [[0,1],[2,3,4]] → two sub-bars per band) ─────
    function renderGroupedStackedBars(groups: number[][]) {
        const numGroups = groups.length;
        // Total grouped-bar width — same cap as single stacked
        const rawGroupW = Math.max(1, xBandW * (1 - barGroupPadding));
        const totalW = Math.max(numGroups * 6, Math.min(rawGroupW, 96));
        // Gap between the two sub-bar stacks — matches the per-bar gap in grouped mode
        const innerGap = Math.max(1, Math.min(3, totalW * 0.03));
        const subBarW = Math.max(2, (totalW - innerGap * (numGroups - 1)) / numGroups);
        const outerPad = (xBandW - totalW) / 2;

        // Precompute top series index per group per column (for rounded corners)
        const topPerGroup: number[][] = groups.map((grp) =>
            Array.from({ length: n }, (_, ci) => {
                for (let k = grp.length - 1; k >= 0; k--) {
                    if ((series[grp[k]]?.data[ci] ?? 0) > 0) return grp[k];
                }
                return -1;
            }),
        );

        // Precompute group totals for total labels
        const groupTotals: number[][] = groups.map((grp) =>
            Array.from({ length: n }, (_, ci) =>
                grp.reduce((acc, si) => acc + (series[si]?.data[ci] ?? 0), 0),
            ),
        );

        const barElems = groups.flatMap((grp, gi) =>
            grp.map((si) => {
                const color = resolvedColors[si];
                const gradId = `bar-grad-${rawId}-${si}`;
                return (
                    <g key={`bar-${si}`} clipPath={`url(#${clipId})`}>
                        {barGradient && (
                            <defs>
                                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={_lighten(color, 0.22)} stopOpacity={1} />
                                    <stop offset="20%" stopColor={color} stopOpacity={1} />
                                    <stop offset="100%" stopColor={color} stopOpacity={1} />
                                </linearGradient>
                            </defs>
                        )}
                        {series[si]?.data.map((v, ci) => {
                            if (ci < viStart || ci > viEnd) return null;
                            if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null;

                            // Stack base within this group
                            const siInGrp = grp.indexOf(si);
                            const base = grp
                                .slice(0, siInGrp)
                                .reduce((acc, k) => acc + (series[k]?.data[ci] ?? 0), 0);

                            const topV = v + base;
                            const barH = Math.max(1, yToSvg(base) - yToSvg(topV));
                            const barTopY = yToSvg(topV);
                            const bx = xToSvg(ci) + outerPad + gi * (subBarW + innerGap);
                            const bRight = bx + subBarW;
                            const bBottom = barTopY + barH;
                            const isTop = topPerGroup[gi][ci] === si;
                            const rx = isTop ? Math.min(barRadius, barH / 2, subBarW / 4) : 0;
                            const delay = animate ? ci * 0.013 + si * 0.02 : 0;
                            const isHovered = hoveredColIdx === ci;
                            const opacity = highlightOnHover && hoveredColIdx !== null
                                ? (isHovered ? 1 : hoverDimOpacity) : 1;

                            const path = rx > 0
                                ? `M ${bx + rx} ${barTopY} H ${bRight - rx} Q ${bRight} ${barTopY} ${bRight} ${barTopY + rx} V ${bBottom} H ${bx} V ${barTopY + rx} Q ${bx} ${barTopY} ${bx + rx} ${barTopY} Z`
                                : `M ${bx} ${barTopY} H ${bRight} V ${bBottom} H ${bx} Z`;

                            return (
                                <React.Fragment key={`bar-${si}-${ci}`}>
                                    <path
                                        d={path}
                                        fill={barGradient ? `url(#${gradId})` : color}
                                        opacity={opacity}
                                        style={{
                                            transformBox: 'fill-box',
                                            transformOrigin: '50% 100%',
                                            transform: `scaleY(${mounted ? 1 : 0})`,
                                            transition: animate
                                                ? `transform 0.46s cubic-bezier(0.34,1.25,0.64,1) ${delay}s, opacity 0.15s ease`
                                                : 'opacity 0.15s ease',
                                            cursor: onDataPointClick ? 'pointer' : 'default',
                                        }}
                                        onClick={onDataPointClick ? () => onDataPointClick(si, ci, v) : undefined}
                                    />
                                    {/* Separator line between stacked segments */}
                                    {base > 0 && barH > 2 && (
                                        <line
                                            x1={bx + 1} x2={bRight - 1}
                                            y1={barTopY + barH} y2={barTopY + barH}
                                            stroke="rgba(255,255,255,0.5)" strokeWidth={0.75}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </g>
                );
            }),
        );

        // Group total labels above each sub-bar
        const totalLabelElems = showTotalsLabel ? (
            <g style={{ pointerEvents: 'none' }}>
                {groups.flatMap((_, gi) =>
                    Array.from({ length: n }, (_, ci) => {
                        if (ci < viStart || ci > viEnd) return null;
                        const tot = groupTotals[gi][ci];
                        if (tot <= 0) return null;
                        const bx = xToSvg(ci) + outerPad + gi * (subBarW + innerGap);
                        const cx = bx + subBarW / 2;
                        const topY = yToSvg(tot);
                        return (
                            <text key={`total-${gi}-${ci}`}
                                x={cx} y={topY - 4}
                                textAnchor="middle" dominantBaseline="auto"
                                fontSize={8.5} fontWeight={700}
                                fill={totalsLabelColor ?? (isDark ? '#64748b' : '#94a3b8')}
                                fontFamily="inherit"
                                opacity={mounted ? 1 : 0}
                                style={{ transition: animate ? 'opacity 0.4s ease 0.3s' : 'none' }}
                            >
                                {yFmt(tot)}
                            </text>
                        );
                    }),
                )}
            </g>
        ) : null;

        return <>{barElems}{totalLabelElems}</>;
    }

    // ── Line series ───────────────────────────────────────────────────────────
    function renderLines() {
        return series.map((s, si) => {
            const color = resolvedColors[si];
            const pts: [number, number][] = [];
            const ptIdxMap: number[] = [];
            for (let i = viStart; i <= viEnd; i++) {
                const v = s.data[i];
                if (typeof v === 'number' && Number.isFinite(v)) {
                    pts.push([xCenter(i), yToSvg(v)]);
                    ptIdxMap.push(i);
                }
            }
            if (!pts.length) return null;
            const d = smoothBezier(pts, smooth);
            const resolvedDot = dotSize !== undefined ? dotSize : strokeWidth + 1;
            const showDot = resolvedDot > 0 && xBandW > 26;
            const delay = animate ? si * 0.1 : 0;
            return (
                <g key={`line-${si}`} clipPath={`url(#${clipId})`}
                    style={{
                        opacity: mounted ? 1 : 0,
                        transition: animate ? `opacity 0.5s ease ${delay}s` : 'none',
                    }}
                >
                    <path d={d} stroke={color} strokeWidth={strokeWidth}
                        fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {showDot && pts.map(([px, py], i) => (
                        <circle key={i} cx={px} cy={py} r={resolvedDot}
                            fill={color} stroke={dotStroke} strokeWidth={1.2}
                            style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                            onClick={onDataPointClick ? () => onDataPointClick(si, ptIdxMap[i], s.data[ptIdxMap[i]] ?? 0) : undefined}
                        />
                    ))}
                    {showDataLabels && pts.map(([px, py], i) => {
                        const val = s.data[ptIdxMap[i]];
                        if (val === undefined) return null;
                        return (
                            <text key={`dlbl-${i}`} x={px} y={py - 8}
                                textAnchor="middle" dominantBaseline="auto"
                                fontSize={9} fontWeight={600} fill={color}
                                fontFamily="inherit" style={{ pointerEvents: 'none' }}
                            >{yFmt(val)}</text>
                        );
                    })}
                </g>
            );
        });
    }

    // ── Area series ───────────────────────────────────────────────────────────
    function renderAreas() {
        const bY = yToSvg(0);
        return series.map((s, si) => {
            const color = resolvedColors[si];
            const gradId = `area-grad-${rawId}-${si}`;
            const pts: [number, number][] = [];
            const ptIdxMap: number[] = [];
            for (let i = viStart; i <= viEnd; i++) {
                const v = s.data[i];
                if (typeof v === 'number' && Number.isFinite(v)) {
                    pts.push([xCenter(i), yToSvg(v)]);
                    ptIdxMap.push(i);
                }
            }
            if (pts.length < 2) {
                // Fall back to a single dot when only one visible data point
                if (pts.length === 1) {
                    const [px, py] = pts[0];
                    return (
                        <g key={`area-${si}`} clipPath={`url(#${clipId})`}
                            style={{
                                opacity: mounted ? 1 : 0,
                                transition: animate ? `opacity 0.55s ease ${si * 0.1}s` : 'none',
                            }}
                        >
                            <circle cx={px} cy={py} r={strokeWidth + 2}
                                fill={color} stroke={dotStroke} strokeWidth={1.5} />
                        </g>
                    );
                }
                return null;
            }
            const lp = smoothBezier(pts, smooth);
            const ap = `${lp} L${pts[pts.length - 1][0]},${bY} L${pts[0][0]},${bY} Z`;
            const delay = animate ? si * 0.1 : 0;
            const resolvedDot = dotSize !== undefined ? dotSize : strokeWidth + 1;
            const showDot = resolvedDot > 0 && xBandW > 26;
            return (
                <g key={`area-${si}`} clipPath={`url(#${clipId})`}
                    style={{
                        opacity: mounted ? 1 : 0,
                        transition: animate ? `opacity 0.55s ease ${delay}s` : 'none',
                    }}
                >
                    <defs>
                        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={isDark ? Math.min(fillOpacity * 1.4, 0.32) : fillOpacity} />
                            <stop offset="60%" stopColor={color} stopOpacity={fillOpacity * 0.12} />
                            <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <path d={ap} fill={`url(#${gradId})`} />
                    <path d={lp} stroke={color} strokeWidth={strokeWidth}
                        fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    {showDot && pts.map(([px, py], i) => (
                        <circle key={i} cx={px} cy={py} r={resolvedDot}
                            fill={color} stroke={dotStroke} strokeWidth={1.2}
                            style={{ cursor: onDataPointClick ? 'pointer' : 'default' }}
                            onClick={onDataPointClick ? () => onDataPointClick(si, ptIdxMap[i], s.data[ptIdxMap[i]] ?? 0) : undefined}
                        />
                    ))}
                    {showDataLabels && pts.map(([px, py], i) => {
                        const val = s.data[ptIdxMap[i]];
                        if (val === undefined) return null;
                        return (
                            <text key={`dlbl-${i}`} x={px} y={py - 8}
                                textAnchor="middle" dominantBaseline="auto"
                                fontSize={9} fontWeight={600} fill={color}
                                fontFamily="inherit" style={{ pointerEvents: 'none' }}
                            >{yFmt(val)}</text>
                        );
                    })}
                </g>
            );
        });
    }

    // ── Reference lines ───────────────────────────────────────────────────────
    function renderReferenceLines() {
        if (!referenceLines?.length) return null;
        return (
            <g style={{ pointerEvents: 'none' }}>
                {referenceLines.map((rl, ri) => {
                    const sy = yToSvg(rl.y);
                    if (sy < MARGIN_TOP || sy > MARGIN_TOP + chartH) return null;
                    const lineColor = rl.color ?? resolvedColors[0] ?? '#888';
                    return (
                        <g key={ri}>
                            <line
                                x1={marginLeft} x2={marginLeft + chartW}
                                y1={sy} y2={sy}
                                stroke={lineColor} strokeWidth={1.5}
                                strokeDasharray={rl.dash ?? '6 3'}
                                opacity={0.8}
                            />
                            {rl.label && (
                                <text
                                    x={marginLeft + chartW - 4} y={sy - 4}
                                    textAnchor="end" dominantBaseline="auto"
                                    fontSize={9} fontWeight={600}
                                    fill={lineColor} fontFamily="inherit"
                                    opacity={0.9}
                                >
                                    {rl.label}
                                </text>
                            )}
                        </g>
                    );
                })}
            </g>
        );
    }

    // ── Pie / Donut ───────────────────────────────────────────────────────────
    function renderPieDonut() {
        const cx = containerW / 2;
        const cy = height / 2;
        const minDim = Math.min(containerW, height);
        const outerR = minDim * 0.4;
        const innerR = type === 'donut' ? outerR * donutInnerRatio : 0;
        const sliceData = series[0]?.data ?? [];
        const total = sliceData.reduce((a, b) => a + b, 0) || 1;
        const GAP_RAD = sliceData.length > 1 ? 0.025 : 0;

        let angle = -Math.PI / 2;
        return (
            <svg width={containerW} height={height} style={{ display: 'block' }}>
                {sliceData.map((v, i) => {
                    const sliceAngle = (v / total) * 2 * Math.PI;
                    const startA = angle + GAP_RAD;
                    const endA = angle + sliceAngle - GAP_RAD;
                    const d = arcPath(cx, cy, outerR, innerR, startA, endA);
                    angle += sliceAngle;

                    const midA = startA + (endA - startA) / 2;
                    const midR = innerR > 0 ? (innerR + outerR) / 2 : outerR * 0.65;
                    const lx = cx + midR * Math.cos(midA);
                    const ly = cy + midR * Math.sin(midA);
                    const pct = ((v / total) * 100).toFixed(0);
                    const color = resolvedColors[i % resolvedColors.length];
                    const delay = animate ? i * 0.06 : 0;

                    return (
                        <g key={`slice-${i}`}>
                            <path
                                d={d}
                                fill={color}
                                style={{
                                    opacity: mounted ? 1 : 0,
                                    transform: mounted ? 'scale(1)' : 'scale(0.85)',
                                    transformOrigin: `${cx}px ${cy}px`,
                                    transformBox: 'view-box',
                                    transition: animate
                                        ? `opacity 0.45s ease ${delay}s, transform 0.45s cubic-bezier(0.34,1.3,0.64,1) ${delay}s`
                                        : 'none',
                                    cursor: 'default',
                                }}
                                onMouseEnter={(e) => {
                                    const svgRect = (e.currentTarget.ownerSVGElement as SVGSVGElement)?.getBoundingClientRect();
                                    const contRect = containerRef.current?.getBoundingClientRect();
                                    if (svgRect && contRect) {
                                        tipPosRef.current = {
                                            cx: e.clientX - contRect.left,
                                            cy: e.clientY - contRect.top,
                                        };
                                        setTipCat({ visible: true, catIdx: i, pieSlice: i });
                                    }
                                }}
                                onMouseLeave={handleSvgLeave}
                            />
                            {/* Percentage label inside slice */}
                            {sliceAngle > pieLabelMinAngle && (
                                <text
                                    x={lx}
                                    y={ly}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fontSize={pieLabelFontSize}
                                    fontWeight={700}
                                    fill="rgba(255,255,255,0.92)"
                                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                                >
                                    {pct}%
                                </text>
                            )}
                        </g>
                    );
                })}
                {/* Donut center label */}
                {type === 'donut' && (
                    <text
                        x={cx}
                        y={cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={donutCenterFontSize}
                        fontWeight={700}
                        fill={isDark ? '#f1f5f9' : '#1e293b'}
                        style={{ userSelect: 'none' }}
                    >
                        {donutLabel ?? fmtNum(sliceData.reduce((a, b) => a + b, 0))}
                    </text>
                )}
            </svg>
        );
    }

    // ── Empty state ───────────────────────────────────────────────────────────
    if (!series.length || !hasData) {
        // Animated skeleton bars — heights mimic a typical multi-series distribution
        const emptyBars = [
            { h: 26, o: 0.38 }, { h: 44, o: 0.48 }, { h: 60, o: 0.58 },
            { h: 52, o: 0.53 }, { h: 38, o: 0.45 }, { h: 20, o: 0.35 },
        ];
        const eBW = 12, eGap = 9;
        const eTotalW = emptyBars.length * (eBW + eGap) - eGap;
        const eSvgH = 66;
        const emptyFill = emptyStateFill
            ? (isDark ? (emptyStateFill.dark ?? '#1e293b') : (emptyStateFill.light ?? '#e2e8f0'))
            : (isDark ? '#1e293b' : '#e2e8f0');
        return (
            <div
                ref={containerRef}
                className={className}
                style={{
                    height,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 14,
                }}
            >
                <svg
                    width={eTotalW} height={eSvgH}
                    viewBox={`0 0 ${eTotalW} ${eSvgH}`}
                    fill="none"
                    style={{ display: 'block', overflow: 'visible' }}
                >
                    {emptyBars.map((b, i) => (
                        <rect key={i}
                            x={i * (eBW + eGap)} y={eSvgH - b.h}
                            width={eBW} height={b.h} rx={3}
                            fill={emptyFill}
                            opacity={b.o}
                            style={{
                                transformBox: 'fill-box',
                                transformOrigin: '50% 100%',
                                transform: `scaleY(${mounted ? 1 : 0})`,
                                transition: animate
                                    ? `transform 0.4s cubic-bezier(0.34,1.2,0.64,1) ${i * 0.055}s`
                                    : 'none',
                            }}
                        />
                    ))}
                </svg>
                <span
                    style={{
                        fontSize: 11.5,
                        color: isDark ? '#475569' : '#94a3b8',
                        fontFamily: 'inherit',
                        letterSpacing: '0.01em',
                    }}
                >
                    {noDataLabel}
                </span>
            </div>
        );
    }

    // ── Main render ───────────────────────────────────────────────────────────
    const svgCursor = isZoomed
        ? (isDragging ? 'grabbing' : 'grab')
        : 'crosshair';

    const cartesianSvg = !isPie && (
        <svg
            ref={svgRef}
            width={svgW}
            height={svgH}
            style={{ display: 'block', overflow: 'hidden', cursor: svgCursor, userSelect: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleSvgLeave}
            onDoubleClick={handleDblClick}
        >
            <defs>
                <clipPath id={clipId}>
                    <rect x={marginLeft} y={MARGIN_TOP} width={chartW} height={chartH + 1} />
                </clipPath>
            </defs>
            {renderGrid()}
            {renderYAxis()}
            {renderXAxis()}
            <g clipPath={`url(#${clipId})`}>
                {renderCrosshair()}
            </g>
            {(type === 'bar' || type === 'stacked' || isStacked || !!stackGroups?.length) && renderBars()}
            {type === 'line' && renderLines()}
            {type === 'area' && renderAreas()}
            {renderReferenceLines()}
        </svg>
    );

    const isLineOrArea = type === 'line' || type === 'area';

    const chartArea = (
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            {isPie ? renderPieDonut() : cartesianSvg}
            <GtChartTooltip
                visible={tipCat.visible && hasData && tooltipRows.length > 0}
                cx={tipPosRef.current.cx}
                cy={tipPosRef.current.cy}
                catLabel={tooltipCatLabel}
                rows={tooltipRows}
                isDark={isDark}
                containerW={containerW}
                height={height}
                isStacked={isStacked || !!stackGroups?.length}
                stackGroups={stackGroups}
                stackGroupLabels={stackGroupLabels}
                formatTooltipValue={formatTooltipValue}
                totalOverride={tooltipTotals != null && tipCat.catIdx >= 0 ? (tooltipTotals[tipCat.catIdx] ?? null) : null}
            />
            {renderZoomControls()}
        </div>
    );

    if (legRight) {
        return (
            <div
                ref={containerRef}
                className={className}
                style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}
            >
                {chartArea}
                <GtChartLegend items={legendItems} pos="right" isDark={isDark} labelColor={labelColor} isLineOrArea={isLineOrArea} isStacked={isStacked} stackGroups={stackGroups} legendScale={legendScale} />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ position: 'relative', width: '100%' }}
        >
            {legTop && <GtChartLegend items={legendItems} pos="top" isDark={isDark} labelColor={labelColor} isLineOrArea={isLineOrArea} isStacked={isStacked} stackGroups={stackGroups} legendScale={legendScale} />}
            {chartArea}
            {legBottom && <GtChartLegend items={legendItems} pos="bottom" isDark={isDark} labelColor={labelColor} isLineOrArea={isLineOrArea} isStacked={isStacked} stackGroups={stackGroups} stackGroupLabels={stackGroupLabels} legendScale={legendScale} />}
        </div>
    );
};

export default GtChart;
